/**
 * 猎域的纯函数：按条件生成候选，以及把查询结果翻译成界面文案。
 * 判定本身在服务端（DNS 然后 RDAP），这里不猜可注册。
 */

const ALPHA = 'abcdefghijklmnopqrstuvwxyz'
const DIGIT = '0123456789'

export const CHARSET_OPTIONS = [
  { value: 'alpha', label: '字母', chars: ALPHA },
  { value: 'digit', label: '数字', chars: DIGIT },
  { value: 'mixed', label: '混合', chars: ALPHA + DIGIT },
  { value: 'custom', label: '自定义', chars: null },
] as const

export type CharsetType = (typeof CHARSET_OPTIONS)[number]['value']
export type InsertPos = 'head' | 'middle' | 'tail'
export type SortMode = 'random' | 'asc' | 'desc'

export type ComposeOpts = {
  charsetType: CharsetType
  customChars: string
  filterChars: string
  insertText: string
  insertPos: InsertPos
  insertArmed: boolean
  sort: SortMode
  suffix: string
  length: number
  count: number
}

export const COMPOSE_DEFAULTS: ComposeOpts = {
  charsetType: 'mixed',
  customChars: '',
  filterChars: '',
  insertText: '',
  insertPos: 'tail',
  insertArmed: false,
  sort: 'random',
  suffix: '.com',
  length: 5,
  count: 20,
}

export const HUNTLY_MESSAGES: Record<string, string> = {
  E_EMPTY_CHARSET: '可用字符集为空，请调整「自定义字符」或「过滤字符」后重试。',
  E_CUSTOM_EMPTY: '选了「自定义」但没有填自定义字符。',
  E_BAD_SUFFIX: '后缀需要以点开头，例如 .com。',
  E_EXHAUSTED: '按当前条件能生成的组合数少于所需个数，已给出全部可能结果。',
}

export const HUNTLY_MAX_BATCH = 2000
export const HUNTLY_QUERY_CHUNK = 25

export type CheckResult = {
  domain: string
  status: string
  verified: boolean
  source?: string
  cached?: boolean
}

export type StatusView = { tone: string; label: string; meta: string }

export function normalizeSuffix(value: string): string {
  const suffix = String(value ?? '').trim().toLowerCase()
  if (suffix === '') return '.com'
  return suffix.charAt(0) === '.' ? suffix : '.' + suffix
}

function resolveCharset(opts: ComposeOpts): { chars: string } | { code: string } {
  const option = CHARSET_OPTIONS.find((item) => item.value === opts.charsetType) ?? CHARSET_OPTIONS[2]
  let base: string
  if (option.value === 'custom') {
    base = String(opts.customChars || '').toLowerCase().replace(/[^a-z0-9-]/g, '')
    if (base === '') return { code: 'E_CUSTOM_EMPTY' }
  } else {
    base = option.chars ?? ''
  }
  const filter = new Set(String(opts.filterChars || '').toLowerCase().split(''))
  const chars = base
    .split('')
    .filter((character, index, all) => !filter.has(character) && all.indexOf(character) === index)
    .join('')
  if (chars === '') return { code: 'E_EMPTY_CHARSET' }
  return { chars }
}

function applyInsert(sld: string, opts: ComposeOpts): string {
  if (!opts.insertArmed) return sld
  const insert = String(opts.insertText || '').toLowerCase().replace(/[^a-z0-9-]/g, '')
  if (insert === '') return sld
  if (opts.insertPos === 'head') return insert + sld
  if (opts.insertPos === 'middle') {
    const mid = Math.floor(sld.length / 2)
    return sld.slice(0, mid) + insert + sld.slice(mid)
  }
  return sld + insert
}

export function sortDomains(list: string[], mode: SortMode | string): string[] {
  const copy = list.slice()
  if (mode === 'asc') return copy.sort()
  if (mode === 'desc') return copy.sort().reverse()
  return copy
}

export function compose(
  opts: Partial<ComposeOpts>,
  random: () => number = Math.random,
): { candidates: string[]; code: string; charset: string; exhausted: boolean } {
  const settings: ComposeOpts = { ...COMPOSE_DEFAULTS, ...opts }
  const charset = resolveCharset(settings)
  if ('code' in charset) {
    return { candidates: [], code: charset.code, charset: '', exhausted: false }
  }
  const chars = charset.chars
  const length = Math.max(1, Math.min(20, Number(settings.length) || 5))
  const count = Math.max(1, Math.min(5000, Number(settings.count) || 20))
  const suffix = normalizeSuffix(settings.suffix)
  const space = Math.pow(chars.length, length)
  const target = Math.min(count, space)
  const seen = new Set<string>()
  const candidates: string[] = []
  const maxAttempts = Math.max(target * 40, 2000)
  let attempts = 0
  while (candidates.length < target && attempts < maxAttempts) {
    attempts += 1
    let sld = ''
    for (let i = 0; i < length; i++) sld += chars.charAt(Math.floor(random() * chars.length))
    const domain = applyInsert(sld, settings) + suffix
    if (seen.has(domain)) continue
    seen.add(domain)
    candidates.push(domain)
  }
  const exhausted = candidates.length < count
  return {
    candidates: sortDomains(candidates, settings.sort),
    code: exhausted ? 'E_EXHAUSTED' : 'OK',
    charset: chars,
    exhausted,
  }
}

export function parseLines(text: string): string[] {
  return String(text || '')
    .split(/[\s,，;；]+/)
    .map((item) => item.trim().toLowerCase())
    .filter(Boolean)
}

export function sanitizeSld(value: string): string {
  return String(value || '')
    .toLowerCase()
    .replace(/^[a-z]+:\/\//, '')
    .replace(/[\/?#].*$/, '')
    .replace(/\..*$/, '')
    .replace(/[^a-z0-9-]/g, '')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
}

export function isDomain(value: string): boolean {
  return /^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?(\.[a-z0-9]{2,})+$/.test(String(value || ''))
}

export function chunk<T>(list: T[], size: number): T[][] {
  const out: T[][] = []
  const step = Math.max(1, size || 1)
  for (let i = 0; i < list.length; i += step) out.push(list.slice(i, i + step))
  return out
}

export function statusLabel(result?: CheckResult | null): StatusView {
  const status = result?.status || 'pending'
  const verified = Boolean(result?.verified)
  if (status === 'available' && verified) {
    return { tone: 'available', label: '可注册', meta: '注册局已确认未被注册' }
  }
  if (status === 'available') {
    return { tone: 'maybe', label: '可能可注册', meta: 'DNS 查不到记录，未经注册局核验' }
  }
  if (status === 'registered') {
    return { tone: 'registered', label: '已注册', meta: '可去售后市场询价' }
  }
  if (status === 'unknown') {
    return { tone: 'unknown', label: '未知', meta: '查询失败或结果不确定' }
  }
  return { tone: 'pending', label: '查询中', meta: '等待查询结果' }
}

export function exportSelection(
  candidates: string[],
  results: Record<string, CheckResult | undefined>,
  filter: string,
): string[] {
  const tone = (domain: string) => statusLabel(results[domain]).tone
  if (filter && filter !== 'all') return candidates.filter((domain) => tone(domain) === filter)
  return candidates.filter((domain) => {
    const t = tone(domain)
    return t === 'available' || t === 'maybe'
  })
}

export function exportLabel(filter: string): string {
  return (
    {
      available: '可注册',
      maybe: '可能可注册',
      registered: '已注册',
      unknown: '未知',
      pending: '查询中',
    } as Record<string, string>
  )[filter] || '可注册'
}

export function countStatuses(results: Record<string, CheckResult | undefined>, domains: string[]) {
  const counts = { available: 0, maybe: 0, registered: 0, unknown: 0, pending: 0 }
  for (const domain of domains) {
    const tone = statusLabel(results[domain]).tone as keyof typeof counts
    if (counts[tone] !== undefined) counts[tone] += 1
  }
  return counts
}

export const ALL_TLDS = [
  'com', 'net', 'org', 'info', 'biz', 'name', 'pro',
  'io', 'ai', 'co', 'me', 'cc', 'tv', 'ws', 'to', 'gg', 'vc', 'fm', 'so',
  'app', 'dev', 'xyz', 'online', 'site', 'website', 'space', 'tech', 'store', 'shop',
  'cloud', 'digital', 'network', 'systems', 'solutions', 'company', 'group', 'club',
  'today', 'world', 'live', 'news', 'blog', 'media', 'video', 'studio', 'design',
  'art', 'photo', 'gallery', 'fun', 'win', 'vip', 'gold',
  'money', 'finance', 'capital', 'fund', 'cash', 'pay',
  'law', 'legal', 'health', 'care', 'fit',
  'food', 'cafe', 'bar', 'wine',
  'travel', 'tours', 'hotel', 'holiday',
  'game', 'games', 'play',
  'ink', 'link', 'page', 'plus', 'one', 'global', 'international',
  'work', 'works', 'team', 'center', 'zone', 'life', 'love', 'style',
  'fashion', 'beauty', 'guru', 'expert', 'consulting', 'agency',
  'email', 'mobile', 'software', 'computer', 'technology', 'science',
  'education', 'academy', 'school', 'university', 'college',
  'city', 'earth', 'land', 'house', 'properties', 'estate',
  'market', 'markets', 'sale', 'deals', 'click', 'top',
]

export const MERCHANTS = [
  { id: 'dynadot', label: 'Dynadot', kind: 'available' as const, note: '去注册可注册的' },
  { id: 'godaddy', label: 'GoDaddy', kind: 'available' as const, note: '去注册可注册的' },
  { id: 'sedo', label: 'Sedo', kind: 'registered' as const, note: '去二手市场询价已注册的' },
  { id: 'dan', label: 'Dan.com', kind: 'registered' as const, note: '去二手市场询价已注册的' },
]

export function merchantUrl(id: string, domains: string[]): string {
  const sample = domains[0] || ''
  const enc = encodeURIComponent(sample)
  if (id === 'sedo') return 'https://sedo.com/search/?keyword=' + enc
  if (id === 'godaddy') return 'https://www.godaddy.com/domainsearch/find?domainToCheck=' + enc + '&checkAvail=1'
  if (id === 'dan') return sample ? 'https://dan.com/buy-domain/' + enc : 'https://dan.com/search'
  if (id === 'dynadot') return 'https://www.dynadot.com/domain/search?domain=' + encodeURIComponent(domains.slice(0, 80).join('\n'))
  return '#'
}
